export type UserPermission = AccessLevel | RoleLevel;

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status?: string;
  phone_number: string;
  user_id: string;
  user_type: string;
  role_name: string;
  single_access_token: string;
  pledge_message_enabled: boolean;
  photo_security_pledge_agreed: string;
  photo_security_pledge_agreed_updated_at: string;
  client: string;
  client_id: string;
  balance: number;
  enable_bim: number;
  is_owner: boolean;
  enable_command_screen_mode: 0 | 1;
  enable_dms_button?: boolean;
  enable_gis_button?: boolean;
  report_builder_enabled?: boolean;
  restrict_observation_creator_delete?: boolean;
  platform_access_mode?: 'V1_ONLY' | 'V2_ONLY' | 'DUAL_ACCESS';
  permissions: UserPermission[];
  roles: Role;
  is_video_report_feature_enabled: boolean;
  custom_fields_enabled: boolean;
  unifier_project_adapter?: 'nwc' | 'default' | (string & {});
  enable_project_unifier?: boolean;
  enable_p6_unifier_sync?: boolean;
  enable_observation_unifier_sync?: boolean;
  otp_required: boolean;
  otp_method: string;
  preferences: {
    language: string;
    is_email_enabled: number;
    interval_observation_emails: number;
    is_observation_email_enabled: number;
  };
  personalization_flag: boolean;
  enable_asif: number;
  enable_measurements: number;
  personalization: {
    dark_mode: boolean;
    primary_color: string;
    accent_color: string;
    secondary_color: string;
    logo: string;
    logo_printing: string;
    logo_icon: string;
    logo_ar: string;
    secondary_name: string;
  };
}

export interface Role {
  name: string;
  permissions: string[];
}

export enum RoleLevel {
  // ────────────────────────────────
  // Organization Level Roles
  // ────────────────────────────────
  ORG_SUPER_ADMIN = 'ORG_SUPER_ADMIN',
  ORG_ADMIN = 'ORG_ADMIN',
  ORG_MEMBER = 'ORG_MEMBER',

  // ────────────────────────────────
  // Project Level Roles
  // ────────────────────────────────
  PROJECT_ADMIN = 'PROJECT_ADMIN',
  PROJECT_EDITOR = 'PROJECT_EDITOR',
  PROJECT_MEMBER = 'PROJECT_MEMBER',
  PROJECT_VIEWER = 'PROJECT_VIEWER',

  // ────────────────────────────────
  // Special Roles
  // ────────────────────────────────
  ORG_OPERATION_ENGINEER = 'ORG_OPERATION_ENGINEER',

  // ────────────────────────────────
  // Deprecated Legacy Roles (mapped to new counterparts)
  // ────────────────────────────────
  PARTICIPANT = 'ORG_MEMBER',
  SUPER_ADMIN = 'ORG_SUPER_ADMIN',
  ADMIN = 'ORG_ADMIN',
  '360_DOCUMENTER' = 'PROJECT_EDITOR',
  QUALITY_MANAGER = 'ORG_ADMIN',
}

export enum AccessLevel {
  // ────────────────────────────────
  // Project & Folder Management
  // ────────────────────────────────
  ADD_FLOOR = 'ADD_FLOOR',
  ADD_HASHTAGS_TO_PROJECTS = 'ADD_HASHTAGS_TO_PROJECTS',
  ADD_MEMBER = 'ADD_MEMBER',
  ADD_PARENT = 'ADD_PARENT',
  ADD_PHASE = 'ADD_PHASE',
  ADD_PHASE_ACTIVITY = 'ADD_PHASE_ACTIVITY',
  ADD_POINTS = 'ADD_POINTS',
  ADD_SUB_FOLDER = 'ADD_SUB_FOLDER',
  ADD_USER = 'ADD_USER',
  ARCHIVE_PROJECTS = 'ARCHIVE_PROJECTS',
  CLOSE_PHASE = 'CLOSE_PHASE',
  CREATE_FOLDER = 'CREATE_FOLDER',
  CREATE_SUB_PROJECTS = 'CREATE_SUB_PROJECTS',
  CREATE_CATEGORY = 'CREATE_CATEGORY',
  CREATE_PROGRESS_REPORT = 'CREATE_PROGRESS_REPORT',
  CREATE_WEEKLY_REPORTS = 'CREATE_WEEKLY_REPORTS',
  CREATE_WEEKLY_REPORTS_COMMENT = 'CREATE_WEEKLY_REPORTS_COMMENT',
  DELEGATE_PROJECT_MANAGER_ROLE = 'DELEGATE_PROJECT_MANAGER_ROLE',
  DELETE_FOLDER = 'DELETE_FOLDER',
  DELETE_FLOOR = 'DELETE_FLOOR',
  DELETE_MEMBER = 'DELETE_MEMBER',
  DELETE_PHASE = 'DELETE_PHASE',
  DELETE_PHASE_ACTIVITY = 'DELETE_PHASE_ACTIVITY',
  DELETE_PROJECT = 'DELETE_PROJECT',
  DELETE_POINTS = 'DELETE_POINTS',
  DELETE_PROGRESS_REPORT = 'DELETE_PROGRESS_REPORT',
  EDIT_FLOOR_NAME = 'EDIT_FLOOR_NAME',
  EDIT_FOLDER = 'EDIT_FOLDER',
  EDIT_MEMBER_ROLE_RESPONSIBILITIES = 'EDIT_MEMBER_ROLE_RESPONSIBILITIES',
  EDIT_MEMBERS = 'EDIT_MEMBERS',
  EDIT_PHASE = 'EDIT_PHASE',
  EDIT_POINT_NAME = 'EDIT_POINT_NAME',
  EDIT_PROJECT = 'EDIT_PROJECT',
  EDIT_USER_ROLE = 'EDIT_USER_ROLE',
  FLOOR_MANAGEMENT = 'FLOOR_MANAGEMENT',
  OPEN_FLOOR_MANAGEMENT = 'OPEN_FLOOR_MANAGEMENT',
  TOGGLE_FLOOR_OBSERVATION_CREATION = 'TOGGLE_FLOOR_OBSERVATION_CREATION',
  LIST_PROGRESS_REPORT = 'LIST_PROGRESS_REPORT',
  MOVE_CATEGORY = 'MOVE_CATEGORY',
  MOVE_PROGRESS = 'MOVE_PROGRESS',
  MOVE_PROJECTS_TO_FOLDER = 'MOVE_PROJECTS_TO_FOLDER',
  MOVE_PROJECTS_TO_LABELS = 'MOVE_PROJECTS_TO_LABELS',
  PROJECT_INFO = 'PROJECT_INFO',
  PROJECT_PANEL_DASHBOARD_BUTTON = 'PROJECT_PANEL_DASHBOARD_BUTTON',
  PROJECT_PLANNING = 'PROJECT_PLANNING',
  REMOVE_USER = 'REMOVE_USER',
  RENAME_CATEGORY = 'RENAME_CATEGORY',
  RENAME_FOLDER = 'RENAME_FOLDER',
  RENAME_REPORT = 'RENAME_REPORT',
  REPLACE_PROJECT_MANAGER = 'REPLACE_PROJECT_MANAGER',
  SET_DEFAULT_VIEW = 'SET_DEFAULT_VIEW',
  START_PHASE = 'START_PHASE',
  SYSTEM_ARCHIVE_PROJECTS = 'SYSTEM_ARCHIVE_PROJECTS',
  SYSTEM_CREATE_PROJECTS = 'SYSTEM_CREATE_PROJECTS',
  SYSTEM_DELETE_PROJECTS = 'SYSTEM_DELETE_PROJECTS',
  SYSTEM_MOVE_PROJECTS_TO_FOLDER = 'SYSTEM_MOVE_PROJECTS_TO_FOLDER',
  SYSTEM_PROJECT_ADD_MEMBER = 'SYSTEM_PROJECT_ADD_MEMBER',
  SYSTEM_UPDATE_PROJECTS = 'SYSTEM_UPDATE_PROJECTS',

  // ────────────────────────────────
  // File & Document Access
  // ────────────────────────────────
  ADD_AUTODESK_ACCOUNT = 'ADD_AUTODESK_ACCOUNT',
  CHANGE_FILE_PRIVACY = 'CHANGE_FILE_PRIVACY',
  CLONE_FILE_ROOT_CATEGORIES = 'CLONE_FILE_ROOT_CATEGORIES',
  CREATE_DOCUMENT = 'CREATE_DOCUMENT',
  DELETE_BIM_FILE = 'DELETE_BIM_FILE',
  DELETE_CATEGORY = 'DELETE_CATEGORY',
  DELETE_FILE = 'DELETE_FILE',
  DOWNLOAD_360_VIDEO = 'DOWNLOAD_360_VIDEO',
  DOWNLOAD_DOCUMENT = 'DOWNLOAD_DOCUMENT',
  DOWNLOAD_FILES = 'DOWNLOAD_FILES',
  DOWNLOAD_VERSION = 'DOWNLOAD_VERSION',
  EDIT_DOCUMENT_INFO = 'EDIT_DOCUMENT_INFO',
  FILES_MANAGEMENT = 'FILES_MANAGEMENT',
  LIST_DELETED_FILES = 'LIST_DELETED_FILES',
  LIST_FILE_CATEGORIES = 'LIST_FILE_CATEGORIES',
  LIST_FILE_MANAGEMENT_HISTORY = 'LIST_FILE_MANAGEMENT_HISTORY',
  MAKE_OLD_VERSION_BECOME_CURRENT_VERSION = 'MAKE_OLD_VERSION_BECOME_CURRENT_VERSION',
  MOVE_FILE = 'MOVE_FILE',
  OPEN_FILE_MANAGEMENT = 'OPEN_FILE_MANAGEMENT',
  OPEN_FILES = 'OPEN_FILES',
  RENAME_DOCUMENT = 'RENAME_DOCUMENT',
  UPDATE_AUTODESK_ACCOUNT = 'UPDATE_AUTODESK_ACCOUNT',
  UPDATE_BIM_OFFSET = 'UPDATE_BIM_OFFSET',
  UPDATE_FILE_PRIVACY = 'UPDATE_FILE_PRIVACY',
  UPLOAD_BIM_FILE = 'UPLOAD_BIM_FILE',
  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
  UPLOAD_FILES = 'UPLOAD_FILES',
  UPLOAD_NEW_VERSION = 'UPLOAD_NEW_VERSION',
  VIEW_BIM_FILE = 'VIEW_BIM_FILE',
  VIEW_CATEGORY = 'VIEW_CATEGORY',
  VIEW_DOCUMENT_INFO = 'VIEW_DOCUMENT_INFO',
  VIEW_FILES = 'VIEW_FILES',
  VIEW_FILES_FOLDERS = 'VIEW_FILES_FOLDERS',
  VIEW_FOLDER = 'VIEW_FOLDER',
  VIEW_FOLDER_INFO = 'VIEW_FOLDER_INFO',
  VIEW_PRIVATE_DOCUMENT = 'VIEW_PRIVATE_DOCUMENT',
  VIEW_PUBLIC_DOCUMENT = 'VIEW_PUBLIC_DOCUMENT',
  VIEW_VERSION = 'VIEW_VERSION',

  // ────────────────────────────────
  // Report Management
  // ────────────────────────────────
  ACCESS_ALL_360_REPORTS = 'ACCESS_ALL_360_REPORTS',
  BUILD_PDF_REPORTS = 'BUILD_PDF_REPORTS',
  CREATE_REPORTS = 'CREATE_REPORTS',
  DELETE_REPORT = 'DELETE_REPORT',
  EDIT_REPORT_NAME = 'EDIT_REPORT_NAME',
  EXPORT_TO_EXCEL = 'EXPORT_TO_EXCEL',
  MERGE_REPORTS = 'MERGE_REPORTS',
  OPEN_REPORT = 'OPEN_REPORT',
  PUBLISH_REPORT = 'PUBLISH_REPORT',
  REPORT_MANAGEMENT = 'REPORT_MANAGEMENT',
  OPEN_REPORT_MANAGEMENT = 'OPEN_REPORT_MANAGEMENT',
  SYSTEM_DOWNLOADS_SNAPSHOTS = 'SYSTEM_DOWNLOADS_SNAPSHOTS',
  SYSTEM_EXPORT_TO_PDF = 'SYSTEM_EXPORT_TO_PDF',
  SYSTEM_OPEN_REPORT = 'SYSTEM_OPEN_REPORT',
  SYSTEM_VIEW_PROJECT_REPORT = 'SYSTEM_VIEW_PROJECT_REPORT',
  VIEW_BY_REPORTS = 'VIEW_BY_REPORTS',
  VIEW_REPORT_INFORMATION = 'VIEW_REPORT_INFORMATION',

  // ────────────────────────────────
  // PPE Auto-Detection (V2CORE-2901)
  // ────────────────────────────────
  TOGGLE_PPE_DETECTION = 'TOGGLE_PPE_DETECTION',
  RUN_PPE_DETECTION = 'RUN_PPE_DETECTION',
  REMOVE_PPE_AI_OBSERVATIONS = 'REMOVE_PPE_AI_OBSERVATIONS',

  // ────────────────────────────────
  // Snag Management
  // ────────────────────────────────
  COMMENT_ON_SNAGS = 'COMMENT_ON_SNAGS',
  CREATE_NEW_SNAG = 'CREATE_NEW_SNAG',
  EDIT_SNAG = 'EDIT_SNAG',
  OPEN_SNAGS = 'OPEN_SNAGS',
  SYSTEM_COMMENT_ON_SNAGS = 'SYSTEM_COMMENT_ON_SNAGS',
  VIEW_PRIVATE_SNAGS = 'VIEW_PRIVATE_SNAGS',
  VIEW_PUBLIC_SNAGS = 'VIEW_PUBLIC_SNAGS',
  VIEW_SNAGS = 'VIEW_SNAGS',

  // ────────────────────────────────
  // Observation Management
  // ────────────────────────────────
  ADD_NEW_OBSERVATION = 'ADD_NEW_OBSERVATION',
  CHANGE_OBSERVATION_STATUS = 'CHANGE_OBSERVATION_STATUS',
  CREATE_REPLY_ON_OBSERVATION = 'CREATE_REPLY_ON_OBSERVATION',
  DELETE_OBSERVATION = 'DELETE_OBSERVATION',
  EDIT_OBSERVATION = 'EDIT_OBSERVATION',
  EDIT_OBSERVATION_CODE = 'EDIT_OBSERVATION_CODE',
  EDIT_OBSERVATION_POINT = 'EDIT_OBSERVATION_POINT',
  FILTER_OBSERVATIONS = 'FILTER_OBSERVATIONS',
  GENERATE_OBSERVATION_SUMMARY_REPORT = 'GENERATE_OBSERVATION_SUMMARY_REPORT',
  OBSERVATION_MANAGEMENT = 'OBSERVATION_MANAGEMENT',
  OPEN_OBSERVATION_DETAILS = 'OPEN_OBSERVATION_DETAILS',
  OPEN_OBSERVATIONS = 'OPEN_OBSERVATIONS',
  OPEN_OBSERVATIONS_MANAGEMENT = 'OPEN_OBSERVATIONS_MANAGEMENT',
  SYSTEM_ACCESS_ALL_OBSERVATIONS = 'SYSTEM_ACCESS_ALL_OBSERVATIONS',
  SYSTEM_OPEN_OBSERVATION_DETAILS = 'SYSTEM_OPEN_OBSERVATION_DETAILS',
  SYSTEM_RESTORE_OBSERVATION = 'SYSTEM_RESTORE_OBSERVATION',
  UPLOAD_OBSERVATION_EVIDENCE = 'UPLOAD_OBSERVATION_EVIDENCE',
  VIEW_ALL_OBSERVATIONS = 'VIEW_ALL_OBSERVATIONS',
  VIEW_WEEKLY_REPORTS = 'VIEW_WEEKLY_REPORTS',

  // ────────────────────────────────
  // Weekly Report Management
  // ────────────────────────────────
  CREATE_SUMMARY_WEEKLY_REPORTS = 'CREATE_SUMMARY_WEEKLY_REPORTS',
  DELETE_SUMMARY_WEEKLY_REPORTS = 'DELETE_SUMMARY_WEEKLY_REPORTS',
  DELETE_WEEKLY_REPORTS = 'DELETE_WEEKLY_REPORTS',
  RESTORE_DELETED_WEEKLY_REPORTS = 'RESTORE_DELETED_WEEKLY_REPORTS',
  UPDATE_SUMMARY_WEEKLY_REPORTS = 'UPDATE_SUMMARY_WEEKLY_REPORTS',
  UPDATE_WEEKLY_REPORTS = 'UPDATE_WEEKLY_REPORTS',
  VIEW_DELETED_WEEKLY_REPORTS = 'VIEW_DELETED_WEEKLY_REPORTS',
  VIEW_SUMMARY_WEEKLY_REPORTS = 'VIEW_SUMMARY_WEEKLY_REPORTS',

  // ────────────────────────────────
  // KPI & Performance Management
  // ────────────────────────────────
  CRUD_INDICATORS = 'CRUD_INDICATORS',
  DELETE_KPI = 'DELETE_KPI',
  EDIT_KPI = 'EDIT_KPI',
  EXPORT_KPI = 'EXPORT_KPI',
  LIST_KPI = 'LIST_KPI',
  LIST_KPI_INDICATORS = 'LIST_KPI_INDICATORS',
  PERFORMANCE_MANAGEMENT = 'PERFORMANCE_MANAGEMENT',
  OPEN_PERFORMANCE_MANAGEMENT = 'OPEN_PERFORMANCE_MANAGEMENT',
  PUBLISH_KPI = 'PUBLISH_KPI',
  SAVE_KPI = 'SAVE_KPI',
  SET_DEFAULT_KPI_WEIGHTS = 'SET_DEFAULT_KPI_WEIGHTS',
  SHOW_PROGRESS_REPORT = 'SHOW_PROGRESS_REPORT',
  UPDATE_PHASE_ACTIVITY = 'UPDATE_PHASE_ACTIVITY',
  UPDATE_PROGRESS_REPORT = 'UPDATE_PROGRESS_REPORT',
  VIEW_KPI = 'VIEW_KPI',
  VIEW_KPI_LATEST = 'VIEW_KPI_LATEST',
  VIEW_PHASE_ACTIVITY = 'VIEW_PHASE_ACTIVITY',
  VIEW_PHASES = 'VIEW_PHASES',

  // ────────────────────────────────
  // Audit Management
  // ────────────────────────────────
  AUDIT_REPORT = 'AUDIT_REPORT',
  CONDUCT_AUDIT = 'CONDUCT_AUDIT',
  CONDUCT_QUESTIONS = 'CONDUCT_QUESTIONS',
  CREATE_AUDIT_REPORT = 'CREATE_AUDIT_REPORT',
  CREATE_QUESTION_LIST = 'CREATE_QUESTION_LIST',
  DELETE_AUDIT_REPORT = 'DELETE_AUDIT_REPORT',
  EDIT_AUDIT_REPORT = 'EDIT_AUDIT_REPORT',
  IMPORT_TO_QUESTION_LIST = 'IMPORT_TO_QUESTION_LIST',
  SUBMIT_QUESTION_LIST = 'SUBMIT_QUESTION_LIST',
  UPDATE_IN_QUESTION_LIST = 'UPDATE_IN_QUESTION_LIST',
  VIEW_AUDIT_REPORT = 'VIEW_AUDIT_REPORT',
  VIEW_INCOMPLETE_AUDIT_REPORT = 'VIEW_INCOMPLETE_AUDIT_REPORT',

  // ────────────────────────────────
  // System-Level Access & Admin
  // ────────────────────────────────
  MEMBER_MANAGEMENT = 'MEMBER_MANAGEMENT',
  OPEN_MEMBER_MANAGEMENT = 'OPEN_MEMBER_MANAGEMENT',
  SYSTEM_ACCESS_360_DOCUMENTER_APPLICATION = 'SYSTEM_ACCESS_360_DOCUMENTER_APPLICATION',
  SYSTEM_ACCESS_ADMIN_PORTAL = 'SYSTEM_ACCESS_ADMIN_PORTAL',
  SYSTEM_ACCESS_ADMIN_SETTINGS = 'SYSTEM_ACCESS_ADMIN_SETTINGS',
  SYSTEM_ACCESS_ANALYTICS_CHARTS = 'SYSTEM_ACCESS_ANALYTICS_CHARTS',
  SYSTEM_ACCESS_MY_PROJECTS = 'SYSTEM_ACCESS_MY_PROJECTS',
  SYSTEM_ACCESS_MY_TEAM_PROJECTS = 'SYSTEM_ACCESS_MY_TEAM_PROJECTS',
  SYSTEM_ACCESS_OBSERVATIONS_ANALYTICS = 'SYSTEM_ACCESS_OBSERVATIONS_ANALYTICS',
  SYSTEM_ACCESS_SHARED_WITH_ME_PROJECTS = 'SYSTEM_ACCESS_SHARED_WITH_ME_PROJECTS',
  SYSTEM_ACCESS_TOOLS = 'SYSTEM_ACCESS_TOOLS',
  SYSTEM_ACTIVATE_OR_DEACTIVATE_THE_USER = 'SYSTEM_ACTIVATE_OR_DEACTIVATE_THE_USER',
  SYSTEM_ADD_HASHTAGS_TO_PROJECTS = 'SYSTEM_ADD_HASHTAGS_TO_PROJECTS',
  SYSTEM_ADD_SUB_FOLDER = 'SYSTEM_ADD_SUB_FOLDER',
  SYSTEM_ADD_USER = 'SYSTEM_ADD_USER',
  SYSTEM_ADJUST_VIDEOS_REPORT = 'SYSTEM_ADJUST_VIDEOS_REPORT',
  SYSTEM_AUTODESK_OBSERVATION = 'SYSTEM_AUTODESK_OBSERVATION',
  SYSTEM_CREATE_FOLDER = 'SYSTEM_CREATE_FOLDER',
  SYSTEM_CREATE_PROJECT = 'SYSTEM_CREATE_PROJECT',
  SYSTEM_CREATE_USER = 'SYSTEM_CREATE_USER',
  SYSTEM_DELETE_FOLDER = 'SYSTEM_DELETE_FOLDER',
  SYSTEM_DELETE_PROJECT = 'SYSTEM_DELETE_PROJECT',
  SYSTEM_EDIT_PROJECT = 'SYSTEM_EDIT_PROJECT',
  SYSTEM_EDIT_USER = 'SYSTEM_EDIT_USER',
  SYSTEM_EDIT_USER_ROLE = 'SYSTEM_EDIT_USER_ROLE',
  SYSTEM_MOVE_PROJECTS_TO_LABELS = 'SYSTEM_MOVE_PROJECTS_TO_LABELS',
  SYSTEM_PUBLISH_VIDEOS_REPORT = 'SYSTEM_PUBLISH_VIDEOS_REPORT',
  SYSTEM_REJECT_REPORT_FLOOR_VIDEO_REPORT = 'SYSTEM_REJECT_REPORT_FLOOR_VIDEO_REPORT',
  SYSTEM_REMOVE_USER = 'SYSTEM_REMOVE_USER',
  SYSTEM_RENAME_FOLDER = 'SYSTEM_RENAME_FOLDER',
  SYSTEM_RESTORE_FILES = 'SYSTEM_RESTORE_FILES',
  SYSTEM_RESTORE_FLOOR = 'SYSTEM_RESTORE_FLOOR',
  SYSTEM_RESTORE_FOLDER = 'SYSTEM_RESTORE_FOLDER',
  SYSTEM_RESTORE_POINT = 'SYSTEM_RESTORE_POINT',
  SYSTEM_RESTORE_REPORT = 'SYSTEM_RESTORE_REPORT',
  SYSTEM_TRASH_RESTORE = 'SYSTEM_TRASH_RESTORE',
  SYSTEM_USER_MANAGEMENT = 'SYSTEM_USER_MANAGEMENT',
  SYSTEM_USER_SEARCH = 'SYSTEM_USER_SEARCH',
  SYSTEM_VIEW_ANALYTICS_CHARTS = 'SYSTEM_VIEW_ANALYTICS_CHARTS',
  SYSTEM_VIEW_DELETED_WEEKLY_REPORTS = 'SYSTEM_VIEW_DELETED_WEEKLY_REPORTS',
  SYSTEM_VIEW_FILES_FOLDERS = 'SYSTEM_VIEW_FILES_FOLDERS',
  SYSTEM_VIEW_FOLDER = 'SYSTEM_VIEW_FOLDER',
  SYSTEM_VIEW_KPI_ANALYTICS = 'SYSTEM_VIEW_KPI_ANALYTICS',
  SYSTEM_VIEW_PENDING_VIDEOS_REPORTS = 'SYSTEM_VIEW_PENDING_VIDEOS_REPORTS',
  SYSTEM_VIEW_PROJECTS = 'SYSTEM_VIEW_PROJECTS',
  SYSTEM_VIEW_USER_PERFORMANCE = 'SYSTEM_VIEW_USER_PERFORMANCE',
  SYSTEM_VIEW_VIDEO_REPORT_DETAILS = 'SYSTEM_VIEW_VIDEO_REPORT_DETAILS',
  SYSTEM_VIEWER_360 = 'SYSTEM_VIEWER_360',

  // ────────────────────────────────
  // Dashboard, Analytics & Charts
  // ────────────────────────────────
  DASHBOARD = 'DASHBOARD',
  PROJECT_COST_DATA = 'PROJECT_COST_DATA',
  PROJECT_PROGRESS = 'PROJECT_PROGRESS',
  PROJECT_REPORTS_CRUD = 'PROJECT_REPORTS_CRUD',

  // ────────────────────────────────
  // UI Interaction & Miscellaneous
  // ────────────────────────────────
  ASSIGN_CAPTURE_REPORT = 'ASSIGN_CAPTURE_REPORT',
  AUTODESK_OBSERVATION = 'AUTODESK_OBSERVATION',
  BACK_TO_DASHBOARD = 'BACK_TO_DASHBOARD',
  BlurHumanFaces = 'BlurHumanFaces',
  CAN_CLOSE_OBSERVATION = 'CAN_CLOSE_OBSERVATION',
  CAPTURE_REPORT = 'CAPTURE_REPORT',
  MEASURE = 'MEASURE',
  OPEN_KPI = 'OPEN_KPI',
  OPEN_TIME_SLIDER = 'OPEN_TIME_SLIDER',
  PLAY_STOP_AUTOTOUR = 'PLAY_STOP_AUTOTOUR',
  PRINTS = 'PRINTS',
  RELOCATE_FLOOR_POINT = 'RELOCATE_FLOOR_POINT',
  RESTORE_WEEKLY_REPORTS = 'RESTORE_WEEKLY_REPORTS',
  SCREEN_SHOT = 'SCREEN_SHOT',
  SEARCH_BY_ID = 'SEARCH_BY_ID',
  SEARCH_USERS = 'SEARCH_USERS',
  SET_IMAGE_DIRECTION = 'SET_IMAGE_DIRECTION',
  SHOW_HIDE_MAP = 'SHOW_HIDE_MAP',
  SHOW_HIDE_OBSERVATIONS = 'SHOW_HIDE_OBSERVATIONS',
  SHOW_HIDE_OBSERVATIONS_ANALYTICS = 'SHOW_HIDE_OBSERVATIONS_ANALYTICS',
  SHOW_HIDE_POINT = 'SHOW_HIDE_POINT',
  SHOW_HIDE_POINT_LIST = 'SHOW_HIDE_POINT_LIST',
  SHOW_HIDE_REPORT_LIST = 'SHOW_HIDE_REPORT_LIST',
  SPLIT_SCREEN = 'SPLIT_SCREEN',
  SWAP_POINT_IMAGES = 'SWAP_POINT_IMAGES',
  UPDATE_PLEDGE_MESSAGE_ENABLED = 'UPDATE_PLEDGE_MESSAGE_ENABLED',
  UPDATE_SETTINGS = 'UPDATE_SETTINGS',
  VIEW_ACTIVE_USERS = 'VIEW_ACTIVE_USERS',
  VIEW_BY_FOLDERS = 'VIEW_BY_FOLDERS',
  VIEW_CLIENT_DETAILS = 'VIEW_CLIENT_DETAILS',
  VIEW_PENDING_VIDEO_REPORT = 'VIEW_PENDING_VIDEO_REPORT',
  VIEW_POINTS = 'VIEW_POINTS',
  VIEW_PROJECT = 'VIEW_PROJECT',
  VIEW_PROJECT_DASHBOARD = 'VIEW_PROJECT_DASHBOARD',
  VIEW_PROJECT_MEMBERS = 'VIEW_PROJECT_MEMBERS',
  VIEWER_360 = 'VIEWER_360',

  // ────────────────────────────────
  // Clone Request
  // ────────────────────────────────
  SEND_CLONE_REQUEST = 'SEND_CLONE_REQUEST',
  VIEW_CLONE_REQUEST = 'VIEW_CLONE_REQUEST',
  SYSTEM_UPDATE_CLONE_REQUEST = 'SYSTEM_UPDATE_CLONE_REQUEST',

  // ────────────────────────────────
  // Unifier Integration
  // ────────────────────────────────
  SYSTEM_UNIFIER_PROJECT_SYNC = 'SYSTEM_UNIFIER_PROJECT_SYNC',
  SYSTEM_UNIFIER_P6_SYNC = 'SYSTEM_UNIFIER_P6_SYNC',
  SYSTEM_UNIFIER_OBSERVATION_MANAGEMENT = 'SYSTEM_UNIFIER_OBSERVATION_MANAGEMENT',
}
